import { useContext } from 'react';

import { Box, IconButton, Typography, makeStyles, Theme, createStyles } from '@material-ui/core';
import { FiChevronLeft, FiMoreVertical } from 'react-icons/fi';

import ChatAvatar from './ChatAvatar';
import { ChatData } from '../../types';
import { DeviceContext } from '../../contexts/deviceContext';

interface Props {
  chat: ChatData;
  onDetailsClick: (chatId: ChatData) => void;
  onClose: () => void;
  showDetailsButton?: boolean;
}

// The open chat's header, as in Laila's panel: back on phones, avatar, name
// and who is in it; the name opens the chat's details.
export const ChatHeader: React.FC<Props> = ({ chat, onDetailsClick, onClose, showDetailsButton = true }) => {
  const classes = useStyles();
  const { isMobile } = useContext(DeviceContext);
  const members = chat.members?.length || 0;
  const subtitle =
    chat.type === 'private' ? 'Private chat' : `${members} ${members === 1 ? 'member' : 'members'}`;

  return (
    <Box className={classes.root}>
      {isMobile && (
        <IconButton size="small" onClick={onClose} aria-label="Back to chats" className={classes.icon}>
          <FiChevronLeft size={24} />
        </IconButton>
      )}
      <Box
        className={classes.identity}
        role={showDetailsButton ? 'button' : undefined}
        onClick={showDetailsButton ? () => onDetailsClick(chat) : undefined}
      >
        <ChatAvatar
          avatarUrl={chat.avatarUrl}
          size={40}
          chatType={chat.type}
          borderWidth={0}
          online={false}
          name={chat.name || undefined}
        />
        <Box minWidth={0}>
          <Typography className={classes.name}>{chat.name}</Typography>
          <Typography variant="caption" color="textSecondary" className={classes.subtitle}>
            {subtitle}
          </Typography>
        </Box>
      </Box>
      {showDetailsButton && (
        <IconButton onClick={() => onDetailsClick(chat)} aria-label="Chat details" className={classes.icon}>
          <FiMoreVertical size={20} />
        </IconButton>
      )}
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flex: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      minHeight: 64,
      padding: '8px 12px 8px 16px',
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      [theme.breakpoints.down('xs')]: { padding: '8px 8px 8px 4px' },
    },
    identity: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      cursor: 'pointer',
    },
    name: {
      fontSize: 15,
      fontWeight: 600,
      lineHeight: '20px',
      color: theme.palette.text.primary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    subtitle: { display: 'block', lineHeight: '18px' },
    icon: { flex: 'none', color: theme.palette.text.secondary },
  })
);
